/**
 * Guest Wishlist Button Component
 * Custom element for adding/removing products from guest wishlists
 * Uses the same plus icon and default button presentation as the project planner app
 */

class GuestWishlistButton extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
    this.handleManagerAvailability();
    window.addEventListener('guestWishlistManagerReady', this.handleManagerAvailability);
  }

  disconnectedCallback() {
    window.removeEventListener('guestWishlistManagerReady', this.handleManagerAvailability);
    this.unsubscribeWishlistChanges?.();
  }

  handleManagerAvailability = () => {
    const button = this.shadowRoot.querySelector('.guest-wishlist-button');
    if (!window.guestWishlistManager) {
      button.disabled = true;
      return;
    }

    button.disabled = false;
    this.updateState();
    if (!this.unsubscribeWishlistChanges) {
      this.unsubscribeWishlistChanges = window.guestWishlistManager.onWishlistChange(() => {
        this.updateState();
      });
    }
  }

  getProductId() {
    return this.getAttribute('data-product-id');
  }

  getVariantId() {
    const productInfo = this.closest('product-info');
    const selectedVariant = productInfo?.querySelector(
      'select[name="id"], input[name="id"]:checked, input[name="id"]'
    );
    return selectedVariant?.value || this.getAttribute('data-variant-id') || this.getAttribute('data-product-id');
  }

  getProductTitle() {
    return this.getAttribute('data-product-title') || 'Product';
  }

  getProductPrice() {
    return parseFloat(this.getAttribute('data-product-price')) || 0;
  }

  getProductImage() {
    return this.getAttribute('data-product-image') || '';
  }

  getProductHandle() {
    return this.getAttribute('data-product-handle') || '';
  }

  getLabel() {
    return this.getAttribute('data-label') || 'Add to Wishlist';
  }

  isFullWidth() {
    return this.getAttribute('data-display') === 'full-width';
  }

  render() {
    const styles = `
      :host {
        --wishlist-color: currentColor;
      }

      .guest-wishlist-button {
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: all 0.3s ease;
        font-size: inherit;
      }

      :host([data-display="full-width"]) {
        display: block;
      }

      :host([data-display="full-width"]) .guest-wishlist-button {
        background: rgb(var(--button-background, 9 40 56));
        border: 1px solid rgb(var(--button-background, 9 40 56));
        border-radius: 12px;
        color: rgb(var(--button-text-color, 255 255 255));
        gap: 4px;
        justify-content: center;
        font-size: 16px;
        height: 50px;
        letter-spacing: 0.64px;
        padding: 13px 16px;
        width: var(--add-to-wishlist-button-width, fit-content);
        transition: 0.2s all ease-in-out;
      }

      :host([data-display="full-width"]) .guest-wishlist-button svg {
        height: 20px;
        width: 20px;
      }

      :host([data-display="full-width"]) .guest-wishlist-button:not(:disabled):hover,
      :host([data-display="full-width"]) .guest-wishlist-button:not(:disabled):focus-visible {
        background: rgb(var(--button-text-color, 255 255 255));
        color: rgb(var(--button-background, 9 40 56));
      }

      .wishlist-button-label {
        font: inherit;
        font-weight: inherit;
      }

      .guest-wishlist-button:hover {
        background-color: rgba(0, 0, 0, 0.05);
      }

      .guest-wishlist-button svg {
        width: 1.5rem;
        height: 1.5rem;
        stroke: currentColor;
        stroke-width: 2;
        fill: none;
        transition: all 0.3s ease;
      }

      .guest-wishlist-button.is-in-wishlist svg {
        fill: none;
        stroke: currentColor;
      }

      .wishlist-tooltip {
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 4px;
        font-size: 0.75rem;
        white-space: nowrap;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        margin-bottom: 0.5rem;
        z-index: 10;
      }

      .guest-wishlist-button:hover .wishlist-tooltip {
        opacity: 1;
      }

      .guest-wishlist-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;

    const template = `
      <style>${styles}</style>
      <button class="guest-wishlist-button" title="${this.getLabel()}" aria-label="${this.getLabel()}">
        ${this.isFullWidth() ? '' : '<div class="wishlist-tooltip">Add to wishlist</div>'}
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d="M5 10H15M10 15L10 5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>
        </svg>
        ${this.isFullWidth() ? `<span class="wishlist-button-label">${this.getLabel()}</span>` : ''}
      </button>
    `;

    this.shadowRoot.innerHTML = template;
  }

  setupEventListeners() {
    const button = this.shadowRoot.querySelector('.guest-wishlist-button');

    button.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (!window.guestWishlistManager) {
        console.warn('Guest wishlist manager not available');
        return;
      }

      this.openProjectSelector();
    });
  }

  openProjectSelector() {
    window.dispatchEvent(new CustomEvent('guestProjectSelectionRequest', {
      detail: {
        item: {
          productId: this.getProductId(),
          variantId: this.getVariantId(),
          title: this.getProductTitle(),
          price: this.getProductPrice(),
          image: this.getProductImage(),
          handle: this.getProductHandle()
        }
      }
    }));
  }

  addToWishlist() {
    if (!window.guestWishlistManager) return;

    // Get or create default wishlist
    let wishlists = window.guestWishlistManager.getAllWishlists();
    let defaultWishlist = wishlists.find(w => w.name === 'My Wishlist' && w.isGuest);

    if (!defaultWishlist) {
      defaultWishlist = window.guestWishlistManager.createWishlist('My Wishlist');
    }

    const item = {
      productId: this.getProductId(),
      variantId: this.getVariantId(),
      title: this.getProductTitle(),
      price: this.getProductPrice(),
      image: this.getProductImage(),
      handle: this.getProductHandle()
    };

    window.guestWishlistManager.addItemToWishlist(defaultWishlist.id, item);
    this.showNotification('Added to wishlist!');
  }

  removeFromWishlist() {
    if (!window.guestWishlistManager) return;

    const productId = this.getProductId();
    const variantId = this.getVariantId();
    const wishlists = window.guestWishlistManager.getWishlistsContainingItem(productId, variantId);

    wishlists.forEach(wishlist => {
      const item = wishlist.items.find(
        i => i.productId == productId && i.variantId == variantId
      );
      if (item) {
        window.guestWishlistManager.removeItemFromWishlist(wishlist.id, item.id);
      }
    });

    this.showNotification('Removed from wishlist');
  }

  updateState() {
    if (!window.guestWishlistManager) return;

    const productId = this.getProductId();
    const variantId = this.getVariantId();
    const button = this.shadowRoot.querySelector('.guest-wishlist-button');
    const isInWishlist = window.guestWishlistManager.isItemInWishlist(productId, variantId);

    if (isInWishlist) {
      button.classList.add('is-in-wishlist');
    } else {
      button.classList.remove('is-in-wishlist');
    }
  }

  showNotification(message) {
    // Dispatch custom event for notification
    const event = new CustomEvent('wishlistNotification', {
      detail: { message }
    });
    window.dispatchEvent(event);
  }
}

customElements.define('guest-wishlist-button', GuestWishlistButton);
